import { MouseEvent, useEffect, useState } from 'react'

import { faFilter as faFilterRegular } from '@fortawesome/pro-regular-svg-icons'
import { faFilter as faFilterSolid } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  IconButton,
  ListSubheader,
  Popover,
  Typography,
} from '@material-ui/core'

import { useCurrentSeniorYear } from 'store/current-senior-year/hooks'

import { CLASSES, CLASS_ABBREVIATIONS, CLASS_LABELS, Class, getGradYearForClass } from './gradYears'

interface ColumnHeaderProps {
  title: string
  selectedClasses: Class[]
  onChange: (next: Class[]) => void
}

const ColumnHeader = ({ title, selectedClasses, onChange }: ColumnHeaderProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [pending, setPending] = useState<Class[]>(selectedClasses)
  const isFiltered = selectedClasses.length !== CLASSES.length
  const currentSeniorYear = useCurrentSeniorYear()

  useEffect(() => {
    if (anchorEl) return
    setPending(selectedClasses)
  }, [anchorEl, selectedClasses])

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setPending(selectedClasses)
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => setAnchorEl(null)

  const togglePending = (cls: Class) => {
    const isSelected = pending.includes(cls)
    const next = isSelected ? pending.filter((c) => c !== cls) : [...pending, cls]
    setPending(CLASSES.filter((c) => next.includes(c)))
  }

  const handleApply = () => {
    onChange(pending)
    setAnchorEl(null)
  }

  const caption = selectedClasses.length ? selectedClasses.map((c) => CLASS_ABBREVIATIONS[c]).join(' • ') : 'None'

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <IconButton
        size="small"
        onClick={handleOpen}
        onMouseDown={(e) => e.stopPropagation()}
        aria-label={`${title} class year selection`}
        color={isFiltered ? 'primary' : 'default'}
        style={{ padding: 2 }}
      >
        <FontAwesomeIcon
          icon={isFiltered ? faFilterSolid : faFilterRegular}
          size="xs"
        />
      </IconButton>
      <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.15 }}>
        <span
          aria-hidden
          style={{ fontSize: 11, fontWeight: 400, visibility: 'hidden', whiteSpace: 'nowrap' }}
        >
          {caption}
        </span>
        <span style={{ fontWeight: 500 }}>{title}</span>
        <span
          style={{
            fontSize: 11,
            fontWeight: 400,
            color: 'rgba(0, 0, 0, 0.54)',
            whiteSpace: 'nowrap',
          }}
        >
          {caption}
        </span>
      </span>
      <Popover
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box width={260}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            pr={2}
          >
            <ListSubheader disableSticky>Classes</ListSubheader>
            <Button
              variant="text"
              size="small"
              color="primary"
              onClick={() => setPending([...CLASSES])}
              disabled={pending.length === CLASSES.length}
            >
              Select all
            </Button>
          </Box>
          <FormGroup style={{ paddingLeft: 16, paddingRight: 16, paddingBottom: 8 }}>
            {CLASSES.map((cls) => (
              <FormControlLabel
                key={cls}
                control={
                  <Checkbox
                    checked={pending.includes(cls)}
                    onChange={() => togglePending(cls)}
                    color="primary"
                    size="small"
                  />
                }
                label={
                  <Box
                    display="flex"
                    alignItems="baseline"
                    gridGap={8}
                  >
                    <Typography
                      variant="body2"
                      style={{ fontWeight: 500 }}
                    >
                      {CLASS_LABELS[cls]}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                    >
                      •
                    </Typography>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                    >
                      {getGradYearForClass(cls, currentSeniorYear)}
                    </Typography>
                  </Box>
                }
              />
            ))}
          </FormGroup>
          <Divider />
          <Box
            display="grid"
            gridGap={8}
            p={1.5}
            style={{ gridTemplateColumns: '1fr 1fr' }}
          >
            <Button
              variant="text"
              color="primary"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleApply}
            >
              Apply
            </Button>
          </Box>
        </Box>
      </Popover>
    </span>
  )
}

export default ColumnHeader
